import React, { FC, useCallback, useEffect, useState } from 'react';
import { useLocation } from '@reach/router';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { AppState } from 'state/types';
import { tablet } from 'styles/breakpoints';

interface ProgressProps {
  step: number;
  questionsLength: number;
  onBackClick?: () => void;
  barColors?: string[];
  barContainerColor?: string;
  hideProgressBar?: boolean;
  hasBackground?: boolean;
}

const Wrapper = styled.div<{ hasBackground?: boolean }>`
  z-index: 3;
  position: relative;
  background: ${({ hasBackground }) =>
    hasBackground ? 'transparent' : '#f3f3f8'};
`;

const Container = styled.div<{
  hideProgressBar?: boolean;
  hasBackground?: boolean;
}>`
  display: flex;
  gap: 0.5rem;
  height: ${({ hideProgressBar }) => (hideProgressBar ? '0' : '0.25rem')};
  transition: height 0.25s;
  background: ${({ hasBackground }) =>
    hasBackground ? 'transparent' : '#f3f3f8'};
  @media ${tablet} {
    margin: 0 1rem;
  }
`;

const ProgressBarContainer = styled.div`
  background: #fff;
  border-radius: 50px;
  flex-grow: 1;
  height: 100%;
`;

const ProgressBar = styled.div<{ value: number; barColors?: string[] }>`
  width: ${({ value }) => value}%;
  height: 100%;
  border-radius: 50px;
  transition: width 0.25s;
  background: ${({ barColors }) => barColors?.[0] || '#F7C800'};
`;

const Line = styled.div`
  height: 0.0625rem;
  background: #848d9f;
`;

const Progress: FC<ProgressProps> = ({
  step,
  questionsLength,
  onBackClick,
  barColors,
  barContainerColor,
  hasBackground,
  hideProgressBar,
  ...props
}) => {
  const [values, setValues] = useState({
    section1: 0,
    section2: 0,
    section3: 0,
    section4: 0,
  });
  const location = useLocation();
  const questions = useSelector((state: AppState) => state.funnel.questions);
  const question = new URLSearchParams(window.location.search).get('q');

  useEffect(() => {
    calculateValue();
  }, [step, question]);

  const calculateSectionCounts = useCallback(() => {
    const sectionCounts = {};

    if (questions?.length) {
      questions.forEach(item => {
        const section = item.section;
        if (sectionCounts[section]) {
          sectionCounts[section]++;
        } else {
          sectionCounts[section] = 1;
        }
      });
    }

    return sectionCounts;
  }, [questions, step]);

  const calculateValue = useCallback(() => {
    if (!questions) return;

    if (question === null || isNaN(Number(question))) return;

    const sectionCounts = calculateSectionCounts();

    const newValues = { section1: 0, section2: 0, section3: 0, section4: 0 };

    if (step <= sectionCounts[1]) {
      newValues.section1 = (100 * step) / sectionCounts[1];
    } else if (step <= sectionCounts[1] + sectionCounts[2]) {
      newValues.section1 = 100;
      newValues.section2 = (100 * (step - sectionCounts[1])) / sectionCounts[2];
    } else if (step <= sectionCounts[1] + sectionCounts[2] + sectionCounts[3]) {
      newValues.section1 = 100;
      newValues.section2 = 100;
      newValues.section3 =
        (100 * (step - sectionCounts[1] - sectionCounts[2])) / sectionCounts[3];
    } else {
      newValues.section1 = 100;
      newValues.section2 = 100;
      newValues.section3 = 100;
      newValues.section4 =
        (100 *
          (step - sectionCounts[1] - sectionCounts[2] - sectionCounts[3])) /
        sectionCounts[4];
    }

    setValues(newValues);
  }, [location, questions, calculateSectionCounts]);

  return (
    <Wrapper {...props} hasBackground={hasBackground}>
      {hideProgressBar ? (
        <Line />
      ) : (
        <Container
          hideProgressBar={hideProgressBar}
          hasBackground={hasBackground}
        >
          <ProgressBarContainer>
            <ProgressBar barColors={barColors} value={values.section1} />
          </ProgressBarContainer>
          <ProgressBarContainer>
            <ProgressBar barColors={barColors} value={values.section2} />
          </ProgressBarContainer>
          <ProgressBarContainer>
            <ProgressBar barColors={barColors} value={values.section3} />
          </ProgressBarContainer>
          <ProgressBarContainer>
            <ProgressBar barColors={barColors} value={values.section4} />
          </ProgressBarContainer>
        </Container>
      )}
    </Wrapper>
  );
};

export default Progress;
